import React, { useState, useEffect } from 'react';
import {
  Form, Input, message, Tooltip, Icon, Select, Button,
} from 'antd';
import SunEditor, { buttonList } from 'suneditor-react';
import PropTypes from 'prop-types';
import uploaderApi from 'utils/api/Uploader/UploaderApi';
import UploaderComponent from 'components/UploaderComponent/UploaderComponent';
import GalleryModal from 'components/GalleryModal/GalleryModal';
import utilsApi from 'utils/api/posts/UtilsApi';
import 'suneditor/dist/css/suneditor.min.css';
import './GeneralOfferForm.less';

const { Option } = Select;

const GeneralOffer = (props) => {
  const {
    form, token, handleRequest, nested, data, renderToEdit,
  } = props;
  const {
    validateFields,
    getFieldDecorator,
    getFieldValue,
    setFieldsValue,
    resetFields,
  } = form;

  const { pathname } = window.location;
  const generalOffer = pathname.split('/')[1] === 'oferta-general';
  /**
   * ExampleImages is used for the antd images array.
   * ImagesArray is the merge between antd images and the media_service response data.
   * RichText is the string from the richText
   */
  const [exampleImages, setExampleImages] = useState([]);
  const [coverImage, setCoverImage] = useState('');
  const [relatedOfferImage, setRelatedOfferImage] = useState('');
  const [imagesArray, setImagesArray] = useState([]);
  const [richText, setRichText] = useState('');

  /**
   * Oneliner function for string capitalization
   * @param {String} string
   * @return String
   */
  const capitalizeString = (string) => string.charAt(0).toUpperCase() + string.slice(1);

  /**
   * colors dictionary for post message
   * @type {{orange: number, red: number, green: number, blue: number}}
   */
  const colorPostMessageDictionary = [
    { color: 'Verde', value: 1 },
    { color: 'Azul', value: 2 },
    { color: 'Naranja', value: 3 },
    { color: 'Rojo', value: 4 },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    validateFields((err, values) => {
      if (!err) {
        const valuesJson = values;
        valuesJson.title = capitalizeString(values.title);
        valuesJson.subtitle = capitalizeString(values.subtitle);
        valuesJson.sample_images = imagesArray.map((i) => i.url);
        valuesJson.message = {};
        if (values.post_message) {
          valuesJson.message.text = values.post_message;
          valuesJson.message.priority = values.priority;
        }
        if (relatedOfferImage && values.related_offer_url) {
          valuesJson.relatedOffer = {
            image_url: relatedOfferImage,
            offer_url: values.related_offer_url,
          };
        }
        delete valuesJson.priority;
        delete valuesJson.post_message;
        handleRequest(valuesJson);
      }
    });
  };

  /**
   * Method to mutate the images Array when is coverImage.
   * @param image
   */
  const handleCoverImage = (img) => {
    setCoverImage(img);
  };

  /**
   * Method to get the url from related offer image.
   * @param image
   */
  const handleRelatedOfferImage = (image) => {
    setRelatedOfferImage(image.url);
  };

  /**
   * Method to mutate the images Array when is examples images array.
   * @param image
   * @param uid
   */
  const handleExampleImages = (image, uid) => {
    setExampleImages([...exampleImages, image.url]);
    // eslint-disable-next-line no-underscore-dangle
    setImagesArray([...imagesArray, { url: image.url, uid, key: image.key }]);
  };

  /**
   * Method to delete the image file.
   * @param key
   * @param index
   */
  const deleteImage = (key, index) => {
    uploaderApi
      .deleteImage({ key: key.toString(), token })
      .then(() => {
        const newImagesArray = imagesArray;
        newImagesArray.splice(index, 1);
        message.success('Imagen borrada');
        setImagesArray([...newImagesArray]);
      })
      .catch((error) => message.error(error.toString()));
  };

  /**
   * Method to handle the related offer image remove
   */
  const handleOnRemoveRelatedOfferImage = () => {
    setRelatedOfferImage('');
  };

  /**
   * Method to manage the file remove.
   * @param file
   */
  const handleOnRemove = (file) => {
    if (imagesArray.length) {
      const index = imagesArray.map((e) => e.uid).indexOf(file.uid);
      const newExampleImagesArray = exampleImages;
      newExampleImagesArray.splice(index, 1);
      setExampleImages(newExampleImagesArray);
      deleteImage(imagesArray[index].key, index);
    }
  };

  /**
   * Method to handle the inputs validator.
   * @param limit
   * @param rule
   * @param value
   * @param callback
   */
  const handleValidator = (limit, rule, value, callback) => {
    try {
      if (value && value.length > limit) {
        callback(`${value.length}/${limit}`);
      }
      callback();
    } catch (err) {
      callback(err);
    }
  };

  /**
   * This method generated a 'random' UUID for the images
   */
  const uuidv4 = () => {
    function p8(s) {
      const p = `${Math.random().toString(16)}000000000`.substr(2, 8);
      return s ? `-${p.substr(0, 4)}-${p.substr(4, 4)}` : p;
    }
    return p8() + p8(true) + p8(true) + p8();
  };

  /**
   * This method retrieves an array of URL's and turn it into
   * objects that the Uploader understands
   * @param {Array} images
   */
  const imagesArrayToFiles = (images) => {
    const imageArray = images.map((e) => {
      const tmp = {
        url: e,
        uid: uuidv4(),
        key: e.split('.com/')[1],
        name: e.split('.com/')[1],
        status: 'done',
      };
      return tmp;
    });
    return imageArray;
  };

  /**
   * useEffect on init and when the exampleImages and richText mutate.
   */
  useEffect(() => {
    form.setFieldsValue({
      sample_images: exampleImages.join(','),
      cover_image: coverImage,
      description: richText,
    });
  }, [coverImage, exampleImages, richText]);

  /**
   * UseEffect to control the form render only when data props changes.
   */
  useEffect(() => {
    // This is only when data props its received with values,
    // this happens when the **Edit** dashboard button was clicked
    if (data && Object.keys(data).length) {
      setFieldsValue({
        lowest_price_url: data.lowest_price_url,
        permalink: data.permalink,
        subtitle: data.subtitle,
        related_offer_url: data.relatedOffer ? data.relatedOffer.offer_url : '',
        post_message: data.message && data.message.text,
        priority:
          data.message && data.message.priority ? data.message.priority : 1,
        title: data.title,
        visas: data.visas,
      });
      setCoverImage(data.cover_image);
      setRichText(data.description);
      setRelatedOfferImage(
        data.relatedOffer ? data.relatedOffer.image_url : '',
      );
      let sampleImages = [];
      if (data.sample_images) {
        sampleImages = imagesArrayToFiles(data.sample_images);
      }
      setImagesArray(sampleImages);
      setExampleImages(data.sample_images);
    }
    // This is only when we have data on the Form and
    // we click again in *Oferta General* sider button to reset the all form data.
    if (data && Object.keys(data).length === 0 && getFieldValue('title')) {
      resetFields();
    }
  }, [data]);

  /**
   * permalinkHandler
   * @returns {Promise<>}
   */
  const getPermalinkHandler = async () => {
    try {
      const permalinkInput = getFieldValue('permalink');
      if (permalinkInput) {
        const { permalink } = await utilsApi.generatePermalink(
          permalinkInput,
          token,
        );
        setFieldsValue({ permalink });
        message.info('Permalink generado');
      }
    } catch (error) {
      message.error('Ocurrió un error al generar el permalink. Code 01');
    }
  };

  /**
   * Method to handle the richt text onChange
   * @param content
   */
  const handleChange = (content) => {
    setRichText(content);
  };

  /**
   * rich text options
   */
  // Remove CodeView button
  const customButtonList = buttonList.complex;
  // Uncomment next line if you want to 'disable' HTML edit in RichText
  // customButtonList[9].pop();
  const richTextOptions = {
    height: 200,
    buttonList: customButtonList,
  };
  return (
    <div>
      <Form onSubmit={handleSubmit} onBlur={handleSubmit}>
        <div className="general-offer-form-first-row">
          <div className="general-offer-form-title-input">
            <Form.Item label="Título">
              {getFieldDecorator('title', {
                rules: [
                  {
                    max: 50,
                    message: 'Caracteres excedidos.',
                    type: 'string',
                  },
                  {
                    required: true,
                    message: 'Por favor inserte el Título',
                  },
                  {
                    validator: (rule, value, callback) => {
                      handleValidator(50, rule, value, callback);
                    },
                  },
                ],
              })(<Input />)}
            </Form.Item>
          </div>
          <div className="general-offer-form-visas-select">
            <Form.Item label="Nº Visas">
              {getFieldDecorator('visas', {
                initialValue: 0,
              })(
                <Select>
                  {Array.from(Array(10).keys()).map((c) => (
                    <Option key={c} value={c}>
                      {c}
                    </Option>
                  ))}
                </Select>,
              )}
            </Form.Item>
          </div>
        </div>
        <div className="general-offer-form-row">
          <div className="general-offer-form-input">
            <Form.Item label="Subtítutlo">
              {getFieldDecorator('subtitle', {
                rules: [
                  {
                    max: 45,
                    message: 'Caracteres excedidos.',
                    type: 'string',
                  },
                  {
                    required: true,
                    message: 'Por favor inserte el Subtítulo',
                  },
                  {
                    validator: (rule, value, callback) => {
                      handleValidator(45, rule, value, callback);
                    },
                  },
                ],
              })(<Input />)}
            </Form.Item>
          </div>
        </div>
        {generalOffer ? (
          <div className="general-offer-form-row">
            <div className="general-offer-form-input">
              <Form.Item label="URL">
                {getFieldDecorator('lowest_price_url', {
                  rules: [
                    {
                      required: true,
                      message: 'Por favor inserte la URL de compra',
                    },
                  ],
                })(<Input />)}
              </Form.Item>
            </div>
          </div>
        ) : null}
        <div className="general-offer-form-row">
          <div className="general-offer-form-input">
            <Form.Item label="Permalink">
              {getFieldDecorator('permalink', {
                rules: [
                  {
                    max: 150,
                    message: 'Caracteres excedidos.',
                    type: 'string',
                  },
                  {
                    required: true,
                    message: 'Por favor inserte el texto',
                  },

                  {
                    validator: (rule, value, callback) => {
                      handleValidator(150, rule, value, callback);
                    },
                  },
                ],
              })(
                <Input
                  disabled={renderToEdit}
                  onBlur={getPermalinkHandler}
                  allowClear
                  suffix={(
                    <Tooltip title="Este texto será transformado en un permalink">
                      <Icon type="info-circle" />
                    </Tooltip>
                  )}
                />,
              )}
            </Form.Item>
          </div>
        </div>
        <div className="general-offer-form-post-message-row">
          <div className="general-offer-form-post-message-input">
            <Form.Item label="Mensaje">
              {getFieldDecorator('post_message', {})(<Input />)}
            </Form.Item>
          </div>
          <div className="general-offer-form-colors-dropdown">
            <Form.Item label="Color">
              {getFieldDecorator('priority', {
                initialValue: 1,
              })(
                <Select>
                  {colorPostMessageDictionary.map((e) => (
                    <Option key={e.color} value={e.value}>
                      <div className="priority-row">{e.color}</div>
                    </Option>
                  ))}
                </Select>,
              )}
            </Form.Item>
          </div>
        </div>
        <div className="general-offer-form-row">
          <div className="general-offer-form-input">
            <Form.Item label="Descripción">
              {renderToEdit ? (
                Object.keys(data).length && (
                  <SunEditor
                    setContents={data.description}
                    onChange={handleChange}
                    setOptions={richTextOptions}
                  />
                )
              ) : (
                // eslint-disable-next-line react/jsx-indent
                <SunEditor
                  onChange={handleChange}
                  setOptions={richTextOptions}
                />
                // eslint-disable-next-line indent
              )}
              {getFieldDecorator('description', {
                initialValue: richText,
                rules: [
                  {
                    required: true,
                    message: 'Por favor inserte una descripción',
                  },
                ],
              })(<Input hidden />)}
            </Form.Item>
          </div>
        </div>
        <div className="general-offer-form-image-row">
          <Form.Item>
            <GalleryModal
              coverImage={coverImage}
              handleCoverImage={handleCoverImage}
              token={token}
            />
            {getFieldDecorator('cover_image', {
              rules: [
                {
                  required: true,
                  message: 'Por favor elija una imagen',
                },
                {
                  validator: (rule, value, callback) => {
                    if (value.length < 2) {
                      callback('Por favor elija una imagen');
                    }
                    callback();
                  },
                },
              ],
            })(<Input hidden />)}
          </Form.Item>
          <Form.Item>
            <UploaderComponent
              customClassName="example-images-list"
              listType="picture"
              limit={6}
              handleImage={handleExampleImages}
              handleOnRemove={handleOnRemove}
              label="Imagenes de Muestra"
              token={token}
              type={2}
              method="post"
              images={
                data.sample_images
                && data.sample_images.length === imagesArray.length
                  ? imagesArray
                  : []
              }
            />
            {getFieldDecorator('sample_images', {})(<Input hidden />)}
          </Form.Item>
        </div>
        <div>
          <Form.Item>
            <UploaderComponent
              customClassName="example-images-list"
              listType="picture"
              limit={1}
              handleImage={handleRelatedOfferImage}
              handleOnRemove={handleOnRemoveRelatedOfferImage}
              label="Oferta relacionada"
              token={token}
              type={3}
              method="post"
              images={
                data.relatedOffer !== undefined && relatedOfferImage.length
                  ? imagesArrayToFiles([relatedOfferImage])
                  : []
              }
            />
            {getFieldDecorator('related_offer_image', {})(<Input hidden />)}
          </Form.Item>
          <Form.Item label="URL de Oferta Relacionada">
            {getFieldDecorator(
              'related_offer_url',
              {},
            )(
              <Input
                suffix={(
                  <Tooltip title="Esta URL es a donde vamos a mandar al cliente si le da click">
                    <Icon type="info-circle" />
                  </Tooltip>
                )}
              />,
            )}
          </Form.Item>
        </div>
        {!nested && (
          <div className="general-offer-form-submit">
            <Form.Item>
              <Button type="primary" htmlType="submit" size="large">
                Guardar Oferta
              </Button>
            </Form.Item>
          </div>
        )}
      </Form>
    </div>
  );
};

GeneralOffer.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  form: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  handleRequest: PropTypes.func.isRequired,
  renderToEdit: PropTypes.bool.isRequired,
  nested: PropTypes.bool,
};

GeneralOffer.defaultProps = {
  nested: false,
};

const GeneralOfferForm = Form.create({ name: 'GeneralOffer' })(GeneralOffer);

export default GeneralOfferForm;
